import { useCallback, useEffect, useRef } from "react";
import { useBeforeUnload } from "react-router-dom";

export const CREATE_RANKING_LEAVE_MESSAGE =
  "You have unsaved changes. Save as draft or publish your ranking before leaving, or your work will be lost.";

/**
 * Warn on tab close, browser back, and in-app link navigation when `when` is true.
 * Call allowLeave() before navigating away after a successful save.
 */
export function useWarnUnsavedChanges(when, message = CREATE_RANKING_LEAVE_MESSAGE) {
  const allowLeaveRef = useRef(false);
  const whenActiveRef = useRef(false);
  whenActiveRef.current = when && !allowLeaveRef.current;

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!whenActiveRef.current) return;
        event.preventDefault();
        event.returnValue = message;
      },
      [message]
    )
  );

  useEffect(() => {
    if (!when) return undefined;

    const onPopState = () => {
      if (allowLeaveRef.current) return;
      const leave = window.confirm(message);
      if (!leave) {
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [when, message]);

  useEffect(() => {
    if (!when) return undefined;

    const onClickCapture = (event) => {
      if (allowLeaveRef.current) return;
      const anchor = event.target.closest("a[href]");
      if (!anchor) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
        return;
      }

      let url;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search &&
        url.hash === window.location.hash
      ) {
        return;
      }

      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, [when, message]);

  const allowLeave = useCallback(() => {
    allowLeaveRef.current = true;
  }, []);

  return { allowLeave };
}
